import React, { useMemo } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { AuditSidePanel } from "./AuditSidePanel";
import { AuditTimeline } from "./AuditTimeline";
import { AuditEmptyState, AuditErrorState, AuditSkeleton } from "./AuditStates";
import { useScheduleAuditLogs } from "./useScheduleAuditLogs";

export interface ScheduleHistoryPanelProps {
    open: boolean;
    onClose: () => void;
    scheduleId: string | number | null | undefined;
    /** shown under the title, e.g. "Jane Doe · Day Shift" */
    subtitle?: string;
    zIndexClass?: string;
}

/** Full change history of one schedule (includes the publish request that published it). */
export const ScheduleHistoryPanel: React.FC<ScheduleHistoryPanelProps> = ({
    open,
    onClose,
    scheduleId,
    subtitle,
    zIndexClass = "z-[1600]",
}) => {
    const query = useMemo(
        () => (open && scheduleId != null && scheduleId !== "" ? { os_emp_schedules_id: scheduleId, include_related: true, order: "desc" as const } : null),
        [open, scheduleId]
    );
    const { logs, meta, isLoading, isLoadingMore, error, loadMore, refresh } = useScheduleAuditLogs(query, 50);

    return (
        <AuditSidePanel
            open={open}
            onClose={onClose}
            title="Schedule history"
            subtitle={subtitle ? `${subtitle} · #${scheduleId}` : `Schedule #${scheduleId}`}
            zIndexClass={zIndexClass}
            actions={
                <button
                    type="button"
                    onClick={refresh}
                    disabled={isLoading}
                    className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Refresh history"
                    title="Refresh (a change made a moment ago can take a second to appear)"
                >
                    <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
                </button>
            }
            footer={
                meta && logs.length > 0 ? (
                    <p className="text-center text-xs text-gray-400">
                        {logs.length} of {meta.total} event{meta.total === 1 ? "" : "s"}
                        {meta.timezone ? ` · times in ${meta.timezone}` : ""}
                    </p>
                ) : undefined
            }
        >
            {isLoading ? (
                <AuditSkeleton />
            ) : error ? (
                <AuditErrorState message={error} onRetry={refresh} />
            ) : logs.length === 0 ? (
                <AuditEmptyState hint="No changes have been recorded for this schedule yet." />
            ) : (
                <>
                    <AuditTimeline logs={logs} timezone={meta?.timezone} groupByDay />
                    {meta?.has_more && (
                        <button
                            type="button"
                            onClick={loadMore}
                            disabled={isLoadingMore}
                            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                            {isLoadingMore && <Loader2 size={14} className="animate-spin" />}
                            Load more
                        </button>
                    )}
                </>
            )}
        </AuditSidePanel>
    );
};

export default ScheduleHistoryPanel;
